import { useInfiniteQuery, useQuery } from 'react-query';
import { LocationEventDetailRepository } from '../../../data/repositories/LocationEventDetailRepository';
import { GetLocationEventDetailUseCase } from '../../../domain/usecases/GetLocationEventDetailUseCase';
import { ILocationEventDetailQuery } from '../../../types/queries/ILocationEventDetailQuery';
import { ILocationEventsQuery } from '../../../types/queries/ILocationEventsQuery';
import type { ILocationQuery } from '../../../types/queries/ILocationQuery';
import type { ILocationsQuery } from '../../../types/queries/ILocationsQuery';
import { INearbyLocation } from '../../../types/queries/INearbyLocationQuery';
import { queryGet } from '../../../utils/queryClient';
import { ILocationPostsQuery } from '../../../types/queries/ILocationPostsQuery';
import { isEmpty } from 'lodash';

export function useLocationsQuery({
  latitude = 37.5555216304,
  longitude = 126.9116794321,
  zoomLevel = 5,
  distanceInMeters,
  artistId,
  memberIds,
  siteTypes,
  status,
  liked,
  kakaoUID
}: ILocationsQuery) {
  async function fetchOfflineLocations() {
    const queryParams = new URLSearchParams({
      latitude: latitude.toString(),
      longitude: longitude.toString(),
      zoom_level: zoomLevel.toString(),
      ...(distanceInMeters && { distance_in_meters: distanceInMeters.toString() }),
      ...(artistId && { artist_id: artistId }),
      ...(memberIds && { member_ids: memberIds.join(',') }),
      ...(siteTypes && { site_types: siteTypes.join(',') }),
      ...(status && { status }),
      ...(liked && { liked: liked.toString() }),
      ...(kakaoUID && { kakao_uid: kakaoUID })
    });

    const url = `/api/v1/locations?${queryParams.toString()}`;
    try {
      const result = await queryGet(url);
      return result;
    } catch (_error) {
      throw new Error('Error fetching data to get useOfflineLocationsQuery');
    }
  }

  const { data, isLoading, isError } = useQuery(
    [
      'OfflineLocations',
      latitude,
      longitude,
      zoomLevel,
      artistId,
      memberIds,
      siteTypes,
      status,
      liked,
      kakaoUID
    ],
    fetchOfflineLocations,
    {
      refetchOnWindowFocus: false
    }
  );

  return {
    data,
    isLoading,
    isError
  };
}

export function useLocationQuery({ locationId, artistId, memberId }: ILocationQuery) {
  async function fetchOfflineLocation() {
    const queryParams = new URLSearchParams({
      ...(artistId && { artist_id: artistId }),
      ...(memberId && { member_id: memberId })
    });

    const url = `/api/v1/locations/${locationId}?${queryParams.toString()}`;
    try {
      const result = await queryGet(url);
      return result;
    } catch (_error) {
      throw new Error('Error fetching data to get useOfflineLocationQuery');
    }
  }

  const { data, isLoading, isError } = useQuery(
    ['OfflineLocation', locationId, artistId, memberId],
    fetchOfflineLocation,
    {
      enabled: !!locationId,
      refetchOnWindowFocus: false
    }
  );

  return {
    data,
    isLoading,
    isError
  };
}

export function useNearByLocationQuery({
  locationId,
  distanceInMeters,
  zoomLevel,
  artistId,
  memberId,
  status,
}: INearbyLocation) {
  async function fetchNearByLocation() {
    const queryParams = new URLSearchParams({
      ...(distanceInMeters && { distance_in_meters: distanceInMeters.toString() }),
      ...(zoomLevel && { zoom_level: zoomLevel }),
      ...(artistId && { artist_id: artistId }),
      ...(memberId && { member_id: memberId }),
      ...(status && { status })
    });

    const url = `/api/v1/locations/${locationId}/nearby?${queryParams.toString()}`;
    try {
      const result = await queryGet(url);
      return result;
    } catch (_error) {
      throw new Error('Error fetching data to get useNearByLocationQuery');
    }
  }

  const { data, isLoading, isError } = useQuery(
    ['NearByLocation', locationId, distanceInMeters, zoomLevel, artistId, memberId],
    fetchNearByLocation,
    {
      enabled: !!locationId,
      refetchOnWindowFocus: false
    }
  );

  return {
    data,
    isLoading,
    isError
  };
}

export function useLocationEventsQuery({
  locationId,
  artistId,
  memberIds,
  status,
  latitude,
  longitude,
}: ILocationEventsQuery) {
  async function fetchLocationEventsQuery() {
    const queryParams = new URLSearchParams({
      ...(status && { status })
    });

    if (locationId) {
      queryParams.append('location_id', locationId);
    }

    if (artistId) {
      queryParams.append('artist_id', artistId);
    }

    if (memberIds && memberIds.length > 0) {
      queryParams.append('member_ids', memberIds.join(','));
    }

    if (latitude) {
      queryParams.append('latitude', latitude.toString());
    }

    if (longitude) {
      queryParams.append('longitude', longitude.toString());
    }

    const url = `/api/v1/locations/events?${queryParams.toString()}`;
    try {
      const result = await queryGet(url);
      return result;
    } catch (_error) {
      throw new Error('Error fetching data to get useLocationEventsQuery');
    }
  }

  const { data, isLoading, isError } = useQuery(
    ['LocationEvents', locationId, artistId, memberIds, status],
    fetchLocationEventsQuery,
    {
      enabled: !!locationId || !!artistId || !isEmpty(memberIds),
      refetchOnWindowFocus: false
    }
  );

  return {
    data,
    isLoading,
    isError
  };
}

export function useLocationPostsQuery({
  locationId,
  eventId,
  artistId,
  memberId,
  perPage
}: ILocationPostsQuery) {
  async function fetchLocationPostsQuery({ pageParam = 1 }) {
    const queryParams = new URLSearchParams({
      per_page: perPage.toString() || '5',
      page: pageParam.toString()
    });

    if (locationId) {
      queryParams.append('location_id', locationId);
    }

    if (eventId) {
      queryParams.append('event_id', eventId);
    }

    if (artistId) {
      queryParams.append('artist_id', artistId);
    }

    if (memberId) {
      queryParams.append('member_id', memberId);
    }

    const url = `/api/v1/locations/posts?${queryParams.toString()}`;
    try {
      const result = await queryGet(url);
      return result;
    } catch (_error) {
      throw new Error('Error fetching paginated data to get useLocationPostsQuery');
    }
  }

  const { data, isLoading, isError, isFetching, fetchNextPage, hasNextPage } = useInfiniteQuery(
    ['LocationPosts', locationId, eventId, artistId, memberId],
    fetchLocationPostsQuery,
    {
      enabled: !!locationId || !!eventId,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, _) => lastPage?.meta?.pagination?.nextPage || undefined
    }
  );

  return {
    data,
    isLoading,
    isError,
    isFetching,
    fetchNextPage,
    hasNextPage
  };
}

export function useLocationEventDetailQuery({ locationId, eventId }: ILocationEventDetailQuery) {
  const repository = new LocationEventDetailRepository();
  const useCase = new GetLocationEventDetailUseCase(repository);

  async function fetchLocationEvent() {
    if (!locationId || !eventId) {
      throw new Error('LocationId and EventId are required');
    }

    try {
      const result = await useCase.execute(locationId, eventId);
      return result;
    } catch (_error) {
      throw new Error('Error fetching data to get useLocationEventDetailQuery');
    }
  }

  const { data, isLoading, isError } = useQuery(
    ['LocationEventDetail', locationId, eventId],
    fetchLocationEvent,
    {
      enabled: !!locationId && !!eventId,
      refetchOnWindowFocus: false
    }
  );

  return {
    data,
    isLoading,
    isError
  };
}
